import { type } from 'arktype';

/**
 * Permission item used in permissions catalog list.
 */
export const managedPermissionSchema = type({
  id: type('string').configure({
    description: 'Semantic permission ID in "resource:action" (or "resource:section:field:action") form.',
    examples: ['persons:read', 'persons:profile:phone:write'],
  }),
  'description?': type('string | null').configure({
    description: 'Human-readable description of what the permission grants. Optional and nullable.',
    examples: ['Read access to person records'],
  }),
  'createdAt?': type('string | Date').configure({
    description: 'ISO 8601 timestamp of when the permission was registered. Optional.',
    examples: ['2026-04-22T08:15:30.000Z'],
  }),
  'featureKey?': type('string | null').configure({
    description: 'Licensable feature key from rbac.permissions.feature_key. Null = foundation, never unlicensed.',
    examples: ['employees.view', null],
  }),
  roleCount: type('number').configure({
    description: 'Number of roles that currently grant this permission.',
    examples: [3],
  }),
}).configure({ description: 'Permission entry as shown in the permissions catalog list.' });

export type ManagedPermission = typeof managedPermissionSchema.t;

/**
 * Role summary attached to a permission.
 */
export const permissionRoleSchema = type({
  id: type('string').configure({
    description: 'Semantic role ID.',
    examples: ['manager', 'team-lead'],
  }),
  'description?': type('string | null').configure({
    description: 'Human-readable role description. Optional and nullable.',
    examples: ['Manages a single team'],
  }),
}).configure({ description: 'Role summary embedded within a permission detail payload.' });

export type PermissionRole = typeof permissionRoleSchema.t;

/**
 * Permission details payload.
 */
export interface PermissionDetails extends ManagedPermission {
  roles: PermissionRole[];
}

/** Direct-array response since the v1 freeze. */
export type PermissionsResponse = ManagedPermission[];

/** Direct-object response since the v1 freeze. */
export type PermissionDetailsResponse = PermissionDetails;
